import { createContext, useContext, useState, useEffect } from 'react'
import { api } from '../utils/api'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setLoading(false)
        return
      }

      const response = await api.get('/auth/verify', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      })

      if (response.data.valid) {
        setUser(response.data.user)
      } else {
        localStorage.removeItem('token')
        setUser(null)
      }
    } catch (error) {
      console.error('Token verification failed:', error)
      localStorage.removeItem('token')
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (username, password) => {
    try {
      const response = await api.post('/auth/login', {
        username,
        password
      })

      const { user: userData, token } = response.data
      
      localStorage.setItem('token', token)
      setUser(userData)
      
      return { success: true, user: userData }
    } catch (error) {
      console.error('Login failed:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      }
    }
  }

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData)

      const { user: newUser, requiresEmailVerification } = response.data
      
      // Don't automatically log in the user - they need to verify email first
      if (requiresEmailVerification) {
        return { 
          success: true, 
          user: newUser, 
          requiresEmailVerification: true,
          message: response.data.message
        }
      }
      
      // If no email verification required (shouldn't happen with new flow)
      localStorage.setItem('token', response.data.token)
      setUser(newUser)
      
      return { success: true, user: newUser }
    } catch (error) {
      console.error('Registration failed:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      }
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setUser(null)
  }

  const forgotPassword = async (email) => {
    try {
      await api.post('/auth/forgot-password', { email })
      return { success: true }
    } catch (error) {
      console.error('Forgot password failed:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Password reset request failed' 
      }
    }
  }

  const resendVerification = async (email) => {
    try {
      const response = await api.post('/auth/resend-verification', { email })
      return { 
        success: true, 
        message: response.data.message 
      }
    } catch (error) {
      console.error('Resend verification failed:', error)
      return { 
        success: false, 
        error: error.response?.data?.message || 'Failed to resend verification email' 
      }
    }
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    resendVerification,
    verifyToken
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
